import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import DarkBlueButton from "../../atoms/buttons/DarkBlueButton";
import WhiteButton from "../../atoms/buttons/WhiteButton";
import CloseButton from "../../atoms/buttons/CloseButton";
import { BsStars } from "react-icons/bs";
import PrescriptionPdf from "../../../screens/PrescriptionPdf";
import { Doctor, Hospital, Booking, Patient } from "../../../lib/utils/types";
import { BookingData } from "../../../services/types";
import { getHosptialDetails } from "../../../lib/apis/hospital";
import { getRouteSegment } from "../../../lib/utils/funcs";
import Select from "react-select";
import {
  getBookingDeatilsByBookingId,
  getDirectPrescriptionViewUrl,
  getPrescriptionViewUrl,
  updateBookingStatus,
} from "../../../lib/apis/booking";
import {
  getPatientByPatientId,
  getPatientByPhoneNo,
  updatePatient,
} from "../../../lib/apis/patient";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useParams } from "react-router-dom";
import { getDoctorAvailability } from "../../../lib/apis/doctor";
import { toast } from "react-toastify";
import {
  getMedicines,
  insertMedicine,
  searchMedicines,
} from "../../../lib/apis/medicines";
import {
  generatePrescriptionWithAI,
  sendPrescriptionTemplateWithPdfLinkDirect,
} from "../../../lib/apis/prescription";

// Firebase Imports
// import { initializeApp, getApp, getApps } from "firebase/app"; // No longer needed here
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "../../../firebase"; // Import storage from your firebase.ts

// Axios for PDF upload to S3-like storage (based on UploadReport.tsx pattern)
import axios from "axios";
import { baseURL } from "../../../lib/utils/constants";
import ReactDropdown from "react-dropdown";
import {
  getPrescriptionTemplatesByDoctorId,
  sendPrescriptionTemplateWithPdfLink,
} from "../../../lib/apis/prescription";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import apiClient from "../../../lib/apis/fetch";

// Define the new Medicine and TemplateData types
interface Medicine {
  name: string;
  dosage: string;
  duration: string;
}
interface Vitals {
  bp?: string;
  weight?: string;
  pulse?: string;
  temperature?: string;
}
interface ExaminationFinding {
  finding: string;
  result: string;
}

interface TemplateData {
  complaints: string;
  historyAndExaminations: string;
  diagnosis: string;
  investigations: string;
  previousMedicines: string;
  medicines: Medicine[];
  advice: string;
  signature: string;
  template_name: string;
  header?: string;
  vitals?: Vitals;
  examinations?: ExaminationFinding[];
}

interface TemplateCategory {
  template_id: string;
  template_name: string;
  data: TemplateData;
}

interface SelectedDoctor {
  name: string;
  mappingId: string;
}

const SendPrescriptionModal = ({
  closeModal,
  modalIsOpen,
  customStyles,
  bookingID,
  patientID,
  patientName,
  bookedDate,
  allBookings,
  type = "booking",
  mappingId,
  templateId,
}: {
  closeModal: () => void;
  modalIsOpen: boolean;
  customStyles: any;
  bookingID: string | null;
  patientID: string | null;
  patientName: string | null;
  bookedDate: string;
  allBookings?: Booking[];
  type?: "booking" | "direct";
  mappingId?: string;
  templateId?: string | null;
}) => {
  const hospital_id = getRouteSegment(1);
  const [hospData, setHospData] = useState<Hospital>();
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [patientCustomName, setPatientCustomName] = useState<string | "">(""); // for patient name
  const [patientContactNumber, setPatientContactNumber] = useState<string | "">(
    ""
  ); // for contact number
  const [bookings, setBookings] = useState<BookingData | undefined>();
  const [docDetails, setDocDetails] = useState<Doctor | undefined>();
  const [patient, setPatient] = useState<Patient | undefined>();
  const param = useParams();
  const [age, setAge] = useState<string | "">(""); // for age
  const [gender, setGender] = useState<string | "">("");

  const mapping_id = String(param.mapping_id);
  const { doctorsData } = useHospDocData();

  const [countryCode, setCountryCode] = useState("91"); // default India

  const countryCodes = [
    { value: "91", label: "India (+91)" },
    { value: "964", label: "Iraq (+964)" },
    { value: "971", label: "Dubai/UAE (+971)" },
    { value: "974", label: "Qatar (+974)" },
  ];

  const getCountryNumberLength = (code: string) => {
    switch (code) {
      case "+91": // India
        return 10;
      case "+964": // Iraq
        return 10; // adjust as per rules
      case "+971": // Dubai/UAE
        return 9;
      case "+974": // Qatar
        return 8;
      default:
        return 10;
    }
  };
  const fetchPatientByContact = useCallback(async () => {
    try {
      const res = await getPatientByPhoneNo(countryCode + patientContactNumber);
      if (res?.status === 200 && res.data.result.length > 0) {
        const data = res.data.result[0];
        setPatientCustomName(data.full_name?.trim());
        setGender(data.gender);
        setAge(data.date_of_birth ? String(getAge(data.date_of_birth)) : "");
        toast.success("Patient details loaded");
      } else {
        toast.error("Patient not found");
        setPatientCustomName("");
        setGender("");
        setAge("");
      }
    } catch (err) {
      console.error("Error fetching patient by number:", err);
      toast.error("Failed to fetch patient details");
    }
  }, [patientContactNumber, countryCode]);

  useEffect(() => {
    if (
      patientContactNumber &&
      patientContactNumber.length === getCountryNumberLength(countryCode)
    ) {
      fetchPatientByContact();
    }
  }, [patientContactNumber, countryCode]);

  // New states for signature handling
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(
    null
  );
  const [isUploadingSignature, setIsUploadingSignature] =
    useState<boolean>(false);

  // New state for PDF upload status
  const [isUploadingPdf, setIsUploadingPdf] = useState<boolean>(false);

  // New state for medicine search
  const [search, setSearch] = useState("");
  const [searchingIndex, setSearchingIndex] = useState<number | null>(null);
  const [medicineNames, setMedicineNames] = useState([{ product_name: "" }]);
  const [searchResults, setSearchResults] = useState<
    { product_name: string }[]
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  const [selectedDoctor, setSelectedDoctor] = useState<SelectedDoctor>({
    name: "",
    mappingId: "",
  });

  // Add debounced search effect
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (search && searchingIndex !== null) {
        setIsSearching(true);
        try {
          const res = await searchMedicines(search);
          if (res?.status === 200) {
            setSearchResults(res.data.result);
          }
        } catch (error) {
          console.error("Error searching medicines:", error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(searchTimeout);
  }, [search, searchingIndex]);

  // Firebase Upload Function
  const uploadSignatureToFirebase = async (file: File): Promise<string> => {
    setIsUploadingSignature(true);
    try {
      // Create a storage reference
      // Using a path like 'signatures/userId/fileName' or 'signatures/bookingId/fileName' is good practice for organization
      // For simplicity here, using a timestamp to ensure uniqueness.
      const storageRef = ref(storage, `signatures/${Date.now()}-${file.name}`);

      console.log(`Uploading ${file.name} to Firebase Storage...`);
      // Upload the file
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Wait for the upload to complete
      await uploadTask;

      // Get the download URL
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      console.log("File available at:", downloadURL);

      // Update formData with the new signature URL immediately
      setFormData((prev) => ({ ...prev, signature: downloadURL }));

      setIsUploadingSignature(false);
      return downloadURL;
    } catch (error) {
      console.error("Error uploading signature to Firebase Storage:", error);
      setIsUploadingSignature(false);
      // Consider showing a user-friendly error message here, e.g., using a toast notification library
      // alert("Signature upload failed. Please try again.");
      throw error; // Re-throw to be caught by the caller if needed (e.g., generatePdf)
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchHosData = async () => {
      if (!isMounted) return;
      const hosp_data = await getHosptialDetails(hospital_id);

      if (hosp_data?.status === 200 && isMounted) {
        setHospData(hosp_data.data.result);
      }
    };

    const fetchBookingsData = async () => {
      if (!isMounted || !bookingID) return;
      try {
        const booked_data = await getBookingDeatilsByBookingId(bookingID);

        if (booked_data?.status === 200 && isMounted) {
          setBookings(booked_data.data.result);
        } else if (isMounted) {
          setBookings(undefined);
          console.error("Failed to fetch booking details:", booked_data);
        }
      } catch (bookingError) {
        if (isMounted) {
          console.error("Error fetching booking details:", bookingError);
          setBookings(undefined);
        }
      }
    };

    const fetchDoctorDeatils = async () => {
      if (!isMounted || (!mapping_id && !selectedDoctor.mappingId)) return;

      try {
        const res = await getDoctorAvailability(
          type === "direct" ? selectedDoctor.mappingId : mapping_id
        );

        if (res?.status === 200 && isMounted) {
          setDocDetails(res.data.result.doctor_details);
        } else if (isMounted) {
          console.error("Failed to fetch doctor details:", res);
        }
      } catch (bookingError) {
        if (isMounted) {
          console.error("Error fetching doctor details:", bookingError);
          setBookings(undefined);
        }
      }
    };

    if (modalIsOpen) {
      fetchDoctorDeatils();
      fetchHosData();
      fetchBookingsData();
    }

    return () => {
      isMounted = false;
    };
  }, [
    hospital_id,
    bookingID,
    mapping_id,
    modalIsOpen,
    selectedDoctor.mappingId,
  ]);

  const [categories, setCategories] = useState<TemplateCategory[]>([
    {
      template_id: "custom",
      template_name: "Custom Prescription",
      data: {
        complaints: "",
        historyAndExaminations: "",
        diagnosis: "",
        investigations: "",
        previousMedicines: "",
        medicines: [],
        examinations: [],

        advice: "",
        signature: "",
        template_name: "Custom Prescription",
        vitals: { bp: "", weight: "", pulse: "", temperature: "" },
      },
    },
  ]);

  useEffect(() => {
    let isMounted = true;

    const fetchPrescriptionTemplatesByDoctorId = async () => {
      if (!isMounted || (!docDetails?.doctor_id && !selectedDoctor.mappingId))
        return;

      try {
        const data = await getPrescriptionTemplatesByDoctorId(
          (docDetails && docDetails.doctor_id) ||
            doctorsData?.filter(
              (doc) => doc.mapping_id === selectedDoctor.mappingId
            )[0]?.doctor_id ||
            ""
        );

        console.log("Fetched templates:", data);

        if (!isMounted) return;

        if (data?.status === 200 && Array.isArray(data.data.result)) {
          const customTemplate = categories[0];
          const apiTemplates = data.data.result
            .map((template: any) => {
              try {
                return {
                  template_id:
                    template.template_id ||
                    `template-${Date.now()}-${Math.random()}`,
                  template_name: template.template_name || "Unnamed Template",
                  data: {
                    complaints: template.data?.complaints || "",
                    historyAndExaminations:
                      template.data?.historyAndExaminations || "",
                    diagnosis: template.data?.diagnosis || "",
                    investigations: template.data?.investigations || "",
                    previousMedicines: template.data?.previousMedicines || "",
                    medicines: Array.isArray(template.data?.medicines)
                      ? template.data.medicines
                      : [],
                    advice: template.data?.advice || "",
                    signature: template.data?.signature || "",
                    template_name: template.template_name || "Unnamed Template",
                  },
                };
              } catch (error) {
                console.error("Error processing template:", error);
                return null;
              }
            })
            .filter(Boolean);

          if (isMounted) {
            setCategories([customTemplate, ...apiTemplates]);
            if (apiTemplates.length > 0) {
              setCurrentTemplateName(customTemplate.template_name);
              setFormData({
                ...customTemplate.data,
                header: patientName || "",
                template_name: customTemplate.template_name,
              });
            }
          }
        } else if (isMounted) {
          console.warn("Invalid API response format:", data);
          setCategories([categories[0]]);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching templates:", error);
          setCategories([categories[0]]);
          toast.error("Failed to load prescription templates");
        }
      }
    };

    if (modalIsOpen && docDetails?.doctor_id) {
      fetchPrescriptionTemplatesByDoctorId();
    }

    return () => {
      isMounted = false;
    };
  }, [docDetails?.doctor_id, selectedDoctor.mappingId, modalIsOpen]);

  let doctorOptions =
    doctorsData
      ?.filter(
        (doctor): doctor is Doctor & { mapping_id: string } =>
          typeof doctor.mapping_id === "string"
      )
      .map((doctor) => ({
        value: doctor.mapping_id,
        label: doctor.full_name,
      })) || [];

  useEffect(() => {
    if (doctorsData) {
      doctorOptions =
        doctorsData
          ?.filter(
            (doctor): doctor is Doctor & { mapping_id: string } =>
              typeof doctor.mapping_id === "string"
          )
          .map((doctor) => ({
            value: doctor.mapping_id,
            label: doctor.full_name,
          })) || [];
    }
  }, [doctorsData]);

  const addExamination = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      examinations: [
        ...(prevFormData.examinations || []),
        { finding: "", result: "" },
      ],
    }));
  };

  const removeExamination = (indexToRemove: number) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      examinations: prevFormData.examinations?.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const handleExaminationChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => {
      const updatedExams = [...(prevFormData.examinations || [])];
      updatedExams[index] = { ...updatedExams[index], [name]: value };
      return { ...prevFormData, examinations: updatedExams };
    });
  };

  useEffect(() => {
    if (mappingId && doctorsData?.length) {
      const doctor = doctorsData.find((d) => d.mapping_id === mappingId);
      if (doctor) {
        setSelectedDoctor({
          name: doctor.full_name,
          mappingId: doctor.mapping_id,
        });
      }
    }
  }, [mappingId, doctorsData]);
  useEffect(() => {
    if (templateId && categories.length > 1) {
      const template = categories.find((t) => t.template_id === templateId);
      if (template) {
        setCurrentTemplateName(template.template_name);
        setFormData({
          ...template.data,
          header: patientName || "",
          template_name: template.template_name,
        });
      }
    }
  }, [templateId, categories, patientName]);

  const [currentTemplateName, setCurrentTemplateName] = useState(
    categories[0]?.template_name
  );

  const [formData, setFormData] = useState<TemplateData>(() => {
    const initialTemplate = categories.find(
      (cat) => cat.template_name === currentTemplateName
    );
    const templateData = initialTemplate?.data || categories[0]?.data;
    return {
      ...templateData,
      header: patientName || "",
      template_name: currentTemplateName || "Custom Prescription",
      vitals: templateData.vitals || {
        bp: "",
        weight: "",
        pulse: "",
        temperature: "",
      },
    };
  });

  const handleVitalsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      vitals: {
        ...prev.vitals,
        [name]: value,
      },
    }));
  };

  function getAge(dateString: string) {
    const today = new Date();
    const dob = new Date(dateString);
    let age = today.getFullYear() - dob.getFullYear();

    // Check if birthday has occurred this year
    const monthDifference = today.getMonth() - dob.getMonth();
    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < dob.getDate())
    ) {
      age--;
    }

    return age;
  }
  // Effect to update signature preview if formData.signature changes (e.g. template load)
  useEffect(() => {
    if (formData.signature && !signatureFile) {
      // If there's an S3 URL in formData and no local file picked
      setSignaturePreviewUrl(formData.signature);
    } else if (!formData.signature && !signatureFile) {
      setSignaturePreviewUrl(null);
    }
    // If signatureFile is set, its preview is handled by handleSignatureFileChange
  }, [formData.signature, signatureFile]);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const resp = await getPatientByPatientId(patientID || "");
        if (resp?.status === 200) {
          setPatient(resp.data.result[0]);
          setGender(resp.data.result[0].gender);
          setAge(
            resp.data.result[0].date_of_birth
              ? String(getAge(resp.data.result[0].date_of_birth))
              : ""
          );
        }
      } catch (err) {
        toast.error("Error fetching patient details");
      }
    };
    fetchPatient();
  }, [patientID]);

  const handleSignatureFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setSignatureFile(file);
      const previewUrl = URL.createObjectURL(file);
      setSignaturePreviewUrl(previewUrl);

      try {
        const firebaseUrl = await uploadSignatureToFirebase(file);
        setFormData((prev) => ({ ...prev, signature: firebaseUrl }));
        console.log(firebaseUrl);

        setSignatureFile(null); // Clear the file state after successful upload to prevent re-upload
        console.log(
          "Signature uploaded successfully to Firebase. URL:",
          formData.signature
        );
      } catch (error) {
        console.error(
          "Signature upload failed during PDF generation process:",
          error
        );
      }
    } else {
      setSignatureFile(null);
      // If no file is selected, and there's no S3 URL in formData, clear preview
      if (!formData.signature) {
        setSignaturePreviewUrl(null);
      }
      // If there IS an S3 URL in formData (e.g. from template), the useEffect above will restore it to preview
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    medicineIndex?: number
  ) => {
    const { name, value } = e.target;

    if (medicineIndex !== undefined) {
      setFormData((prevFormData) => {
        const updatedMedicines = [...prevFormData.medicines];
        if (updatedMedicines[medicineIndex]) {
          updatedMedicines[medicineIndex] = {
            ...updatedMedicines[medicineIndex],
            [name]: value,
          };
        }
        return {
          ...prevFormData,
          medicines: updatedMedicines,
        };
      });

      // Update search state when medicine name is being edited
      if (name === "name") {
        setSearch(value);
        setSearchingIndex(medicineIndex);
      }
    } else {
      setFormData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const addMedicine = () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      medicines: [
        ...prevFormData.medicines,
        { name: "", dosage: "", duration: "" }, // Add a new blank medicine
      ],
    }));
  };

  const removeMedicine = (indexToRemove: number) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      medicines: prevFormData.medicines.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const handleCategoryClick = (templateName: string) => {
    setCurrentTemplateName(templateName);
    const selectedTemplate = categories.find(
      (t) => t.template_name === templateName
    );
    if (selectedTemplate) {
      // Ensure header and template_name are preserved or re-applied when switching templates
      setFormData({
        ...selectedTemplate.data,
        header: formData.header || patientName || "",
        template_name: templateName,
      });
    }
  };

  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setIsGenerating(true);

    try {
      // Create a copy of formData with template_name
      const dataToSend = {
        ...formData,
        template_name: currentTemplateName || "Custom Prescription",
        vitals: formData.vitals || {
          bp: "",
          weight: "",
          pulse: "",
          temperature: "",
        },
      };

      const res = await generatePrescriptionWithAI(dataToSend, aiPrompt);

      if (res?.status === 200) {
        const updated = res.data.result;
        updated.signature = formData.signature;
        setFormData(updated);
      } else {
        toast.error("Failed to generate prescription");
      }
    } catch (err) {
      console.error("AI generation error:", err);
      toast.error("Failed to generate prescription");
    } finally {
      setAiPrompt("");
      setIsGenerating(false);
    }
  };

  const generatePdf = async () => {
    if (
      type === "direct" &&
      (patientCustomName.trim() === "" || patientContactNumber.trim() === "")
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }
    const container = document.getElementById("prescription");
    if (!container) return;
    if (!formData.signature?.trim()) {
      toast.error("Add and upload doctor's signature first.");
      return;
    }
    setLoading(true);

    try {
      // 1) Setup jsPDF for A4
      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      });
      const marginMM = 10;
      const pageWmm = pdf.internal.pageSize.getWidth(); // 210
      const pageHmm = pdf.internal.pageSize.getHeight(); // 297
      const contentWmm = pageWmm - 2 * marginMM; // 190
      const contentHmm = pageHmm - 2 * marginMM; // 277

      // 2) Render once at scale=2 to measure
      const scale = 2;
      const containerWpx = container.clientWidth; // e.g. 450px
      const testCanvas = await html2canvas(container, {
        scale,
        logging: false,
      });
      const testCanvasWpx = testCanvas.width; // = containerWpx * scale
      const mmPerPx = contentWmm / testCanvasWpx; // mm / px
      const maxPagePx = contentHmm / mmPerPx; // how many px fit on one page

      // 3) Break into sections with improved pagination
      const sections = Array.from(container.children) as HTMLElement[];
      interface Page {
        els: HTMLElement[];
        totalPx: number;
      }
      const pages: Page[] = [];
      let current: Page = { els: [], totalPx: 0 };

      // Helper function to check if an element is a medicine section
      const isMedicineSection = (el: HTMLElement) => {
        return (
          el.classList.contains("medicine-section") ||
          el.querySelector(".medicine-item") !== null
        );
      };

      for (const el of sections) {
        const rect = el.getBoundingClientRect();
        const hPx = rect.height * scale; // px at your html2canvas scale

        // If this is a medicine section and it would overflow, start a new page
        if (
          isMedicineSection(el) &&
          current.totalPx + hPx > maxPagePx &&
          current.els.length
        ) {
          pages.push(current);
          current = { els: [], totalPx: 0 };
        }

        // If adding this element would overflow and we already have content, start a new page
        if (current.totalPx + hPx > maxPagePx && current.els.length) {
          pages.push(current);
          current = { els: [], totalPx: 0 };
        }

        current.els.push(el);
        current.totalPx += hPx;
      }
      if (current.els.length) pages.push(current);

      // 4) Render each page off-screen and add to PDF
      for (let i = 0; i < pages.length; i++) {
        const { els } = pages[i];

        // build a wrapper AT THE ORIGINAL CSS WIDTH
        const wrapper = document.createElement("div");
        wrapper.style.width = `${containerWpx}px`;
        wrapper.style.background = "#fff";
        wrapper.style.position = "absolute";
        wrapper.style.top = "-9999px";
        wrapper.style.left = "-9999px";

        // clone & append sections
        els.forEach((e) => wrapper.appendChild(e.cloneNode(true)));
        document.body.appendChild(wrapper);

        // snapshot at the same scale
        const pageCanvas = await html2canvas(wrapper, {
          scale,
          logging: false,
          useCORS: true,
          allowTaint: true,
        });
        document.body.removeChild(wrapper);

        // convert to image & add to PDF with correct aspect ratio
        const imgData = pageCanvas.toDataURL("image/jpeg", 0.95);
        const imgWmm = contentWmm;
        const imgHmm = (pageCanvas.height * imgWmm) / pageCanvas.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", marginMM, marginMM, imgWmm, imgHmm);
      }

      // 5) Turn to blob + File
      const pdfBlob = pdf.output("blob");
      const pdfFile = new File(
        [pdfBlob],
        `prescription-${bookingID}-${patientID}-${Date.now()}.pdf`,
        { type: "application/pdf" }
      );

      // 6) Upload via presigned URL
      setIsUploadingPdf(true);
      let presignedResp;
      if (type === "booking") {
        presignedResp = await apiClient.post(
          `${baseURL}/prescriptions/put-presigned-url`,
          { bookingId: bookingID, patientId: patientID }
        );
      } else {
        presignedResp = await apiClient.post(
          `${baseURL}/prescriptions/sendtopatientdirectly/put-presigned-url`,
          {
            patient_contact_number: patientContactNumber,
            patient_full_name: patientCustomName,
            hospital_id: hospital_id,
            doctor_id: doctorsData?.filter(
              (doc) => doc.mapping_id === selectedDoctor.mappingId
            )[0].doctor_id,
            prescription_date_tz: bookedDate,
          }
        );
      }

      const uploadUrl =
        type === "direct"
          ? presignedResp.data.result.url
          : presignedResp.data.url;

      if (!uploadUrl) throw new Error("Failed to get presigned URL.");

      await axios.put(uploadUrl, pdfFile, {
        headers: { "Content-Type": pdfFile.type },
      });
      setIsUploadingPdf(false);

      // 7) Fetch view URL
      let viewResp;
      if (type === "direct") {
        viewResp = await getDirectPrescriptionViewUrl(
          presignedResp.data.result.direct_prescription_id
        );
      } else {
        viewResp = await getPrescriptionViewUrl(
          bookingID || "",
          patientID || ""
        );
      }
      if (viewResp.status !== 200) {
        toast.error("Failed to retrieve prescription URL.");
        return;
      }
      const viewUrl =
        type === "direct" ? viewResp.data.result.url : viewResp.data.url;

      // 7.5) Update patient gender and date of birth based on age
      if (patientID && gender) {
        let date_of_birth = undefined;
        if (age && !isNaN(Number(age))) {
          // Calculate DOB as today minus age in years
          const years = Number(age);
          const today = new Date();
          const dob = new Date(
            today.getFullYear() - years,
            today.getMonth(),
            today.getDate()
          );
          date_of_birth = dob.toISOString().slice(0, 10); // YYYY-MM-DD
        }
        try {
          const updateRes = await updatePatient(patientID, {
            full_name: patientName || "",
            contact_number: patient?.contact_number,
            gender: gender,
            date_of_birth: date_of_birth || "",
          });
          if (updateRes?.status === 200) {
            toast.success("Patient details updated");
          } else {
            toast.error("Failed to update patient details");
          }
        } catch (err) {
          toast.error("Error updating patient details");
        }
      }

      // 8) Update booking statuses
      if (allBookings) {
        const ongoing = allBookings.find((b) => b.status === 2);
        if (ongoing) {
          await updateBookingStatus({
            bookingId: ongoing.booking_id,
            status: 3,
            doctor_mapping_id: ongoing.transferred_doctor_mapping_id || ongoing.doctor_mapping_id,
          });
        }
        const next = allBookings.find((b) => b.status === 1);
        if (next) {
          await updateBookingStatus({ bookingId: next.booking_id, status: 2 });
        }
      }
      let msgResp;
      // 9) Send link via API
      if (type === "booking") {
        msgResp = await sendPrescriptionTemplateWithPdfLink(
          bookingID || "",
          viewUrl
        );
      } else {
        console.log([
          countryCode + patientContactNumber,
          viewUrl,
          patientCustomName,
          hospital_id,
          selectedDoctor.name,
          bookedDate,
        ]);

        msgResp = await sendPrescriptionTemplateWithPdfLinkDirect(
          countryCode + patientContactNumber,
          viewUrl,
          patientCustomName,
          hospital_id,
          selectedDoctor.name,
          bookedDate
        );
      }
      if (msgResp.status === 200) {
        toast.success("Prescription sent successfully");
      } else {
        toast.error("Failed to send prescription");
      }

      // 10) Close
      closeModal();
    } catch (err) {
      console.error(err);
      toast.error(
        "Failed to generate or upload prescription PDF. Please try again."
      );
    } finally {
      setLoading(false);
      setIsUploadingPdf(false);
    }
  };

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      ariaHideApp={false}
    >
      {/* Header */}
      <div className="px-6 py-3 flex flex-row items-center justify-between border-b-[0.5px] border-sbBorder ">
        <p className="text-dark text-md font-semibold">Send Prescription</p>
        <CloseButton
          handleClick={() => {
            closeModal();
          }}
        />
      </div>
      {isGenerating && (
        <div className="absolute inset-0 bg-black opacity-40">
          <div className="flex items-center justify-center h-full">
            <div className="p-4 rounded-lg shadow-lg">
              <img
                src="https://ik.imagekit.io/ok2wgebfs/Animation%20-%201746713902950%20(1).gif?updatedAt=1746715856238"
                alt=""
                className="w-60"
              />
            </div>
          </div>
        </div>
      )}
      <div className="w-full bg-white rounded-lg shadow-lg">
        <div className=" flex flex-col md:flex-row">
          <div
            className={
              "w-full md:w-[30%] hidden md:flex flex-row md:flex-col border-r-[1px] border-[#d5d9dc]  overflow-y-auto side_bar " +
              (preview ? "!hidden xl:!flex" : "flex")
            }
          >
            {categories.map((item) => (
              <h2
                className={`w-full flex items-center p-4 justify-center text-md text-black  border-b-[1px] border-[#d5d9dc] text-center  cursor-pointer ${
                  item.template_name === currentTemplateName
                    ? "bg-[#335FE4] text-white"
                    : ""
                }`}
                onClick={() => handleCategoryClick(item.template_name)}
                key={item.template_id}
              >
                {item.template_name}
              </h2>
            ))}
          </div>
          <div
            className={
              "w-[90%] flex md:hidden lg:hidden gap-3 px-8 mt-4 items-center overflow-x-auto side_bar_prescription " +
              (preview ? "!hidden " : "")
            }
            style={{ flexWrap: "nowrap" }}
          >
            {categories.map((item) => (
              <h2
                className={`w-max px-2 flex items-center  justify-center text-sm py-2 text-black rounded-md  border-[1px] border-[#d5d9dc] text-center cursor-pointer ${
                  item.template_name === currentTemplateName
                    ? "bg-[#335FE4] text-white"
                    : ""
                }`}
                onClick={() => handleCategoryClick(item.template_name)}
                key={item.template_id}
                style={{ flexShrink: 0 }}
              >
                {item.template_name}
              </h2>
            ))}
          </div>
          <div
            className={
              "w-full flex flex-col items-center justify-between p-6 md:p-4 border-r-[1px] border-[#d5d9dc] " +
              (preview ? "hidden lg:flex !w-full lg:!w-full" : "")
            }
          >
            <div className="w-full max-h-[45vh] xl:max-h-[50vh] overflow-y-auto side_bar p-2 space-y-4">
              {type === "direct" && (
                <>
                  <h1 className="block text-lg font-semibold text-gray-800 mb-2">
                    Doctor Details
                  </h1>
                  <div className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 mb-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <Select
                          options={doctorOptions}
                          value={doctorOptions.find(
                            (opt) => opt.value === selectedDoctor.mappingId
                          )}
                          onChange={(option: any) =>
                            setSelectedDoctor({
                              name: String(option?.label),
                              mappingId: String(option?.value),
                            })
                          }
                          placeholder="Search or select a doctor"
                          isSearchable={true} // 👈 enables search
                          className="w-full"
                          maxMenuHeight={200}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <h1 className="block text-lg font-semibold text-gray-800 mb-2">
                Patient Details
              </h1>
              <div className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 mb-2">
                  {type === "direct" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-700">*</span>
                        </label>
                        <input
                          type="text"
                          name="patientCustomName"
                          value={patientCustomName}
                          onChange={(e) => setPatientCustomName(e.target.value)} // Update the state on change
                          className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Deepraj"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Number <span className="text-red-700">*</span>
                        </label>
                        <div className="flex gap-2">
                          <Select
                            options={countryCodes}
                            value={countryCodes.find(
                              (c) => c.value === countryCode
                            )}
                            onChange={(option) =>
                              setCountryCode(option?.value || "+91")
                            }
                            className="w-full"
                            isSearchable
                          />
                          <input
                            type="text"
                            name="patientContactNumber"
                            value={patientContactNumber}
                            onChange={(e) =>
                              setPatientContactNumber(e.target.value)
                            }
                            className=" w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="888XXXXXXX"
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Age
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={age}
                      onChange={(e) => setAge(e.target.value)} // Update the state on change
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., 30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    {/* <input
                      type="text"
                      name="gender"
                      value={gender} // Bind the value to the state
                      onChange={(e) => setGender(e.target.value)} // Update the state on change
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Female/Male"
                    /> */}
                    <ReactDropdown
                      options={[
                        { label: "Select gender", value: "" },
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                        { label: "Other", value: "Other" },
                      ]}
                      onChange={(e) => setGender(e.value)}
                      value={gender}
                      placeholder={"Select gender"}
                      controlClassName="!mt-2 w-full !p-2 !border !border-gray-300 !rounded-md !shadow-sm focus:!ring-indigo-500 focus:!border-indigo-500"
                      menuClassName="overflow-x-hidden side_bar rounded-lg !mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* ✅ Patient Vitals Section (moved above Description) */}
              <h1 className="block text-lg font-semibold text-gray-800 mb-2">
                Patient Vitals
              </h1>
              <div className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="vitals_bp"
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      Blood Pressure (BP)
                    </label>
                    <input
                      type="text"
                      id="vitals_bp"
                      name="bp"
                      value={formData.vitals?.bp || ""}
                      onChange={handleVitalsChange}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., 120/80 mmHg"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="vitals_weight"
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      Weight
                    </label>
                    <input
                      type="text"
                      id="vitals_weight"
                      name="weight"
                      value={formData.vitals?.weight || ""}
                      onChange={handleVitalsChange}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., 75 kg"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="vitals_pulse"
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      Pulse
                    </label>
                    <input
                      type="text"
                      id="vitals_pulse"
                      name="pulse"
                      value={formData.vitals?.pulse || ""}
                      onChange={handleVitalsChange}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., 72 bpm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="vitals_temperature"
                      className="block text-xs font-medium text-gray-600 mb-1"
                    >
                      Temperature
                    </label>
                    <input
                      type="text"
                      id="vitals_temperature"
                      name="temperature"
                      value={formData.vitals?.temperature || ""}
                      onChange={handleVitalsChange}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g., 98.6 °F"
                    />
                  </div>
                </div>
              </div>

              <h1 className="block text-lg font-semibold text-gray-800 mb-2">
                Description
              </h1>
              {[
                {
                  name: "complaints",
                  label: "Chief Complaints",
                  placeholder: "e.g., Fever for 3 days, persistent headache",
                },
                {
                  name: "historyAndExaminations",
                  label: "History & Examinations",
                  placeholder:
                    "e.g., Patient reports similar episodes. On examination, temp 101°F, mild dehydration.",
                },
                {
                  name: "investigations",
                  label: "Investigations",
                  placeholder: "e.g., CBC, Blood Sugar, X-Ray Chest PA view",
                },
                {
                  name: "diagnosis",
                  label: "Diagnosis",
                  placeholder: "e.g., Viral Fever, Acute Gastritis",
                },
                {
                  name: "previousMedicines",
                  label: "Previous Medicines",
                  placeholder: "e.g., Paracetamol 500mg (SOS for fever)",
                },
              ].map((field) => (
                <div key={field.name} className="mb-4">
                  <label
                    htmlFor={field.name}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {field.label}
                  </label>
                  <textarea
                    id={field.name}
                    name={field.name}
                    value={
                      formData[
                        field.name as keyof Omit<
                          TemplateData,
                          "medicines" | "header" | "vitals" | "examinations"
                        >
                      ]
                    }
                    onChange={handleInputChange}
                    className="mt-1 w-full p-2 border border-gray-300 rounded-md h-20 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={field.placeholder}
                  />

                  {/* ✅ Insert "Add Examination Findings" Section just below History & Examinations */}
                  {field.name === "historyAndExaminations" && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Examination Findings
                      </label>

                      {formData.examinations?.map((exam, index) => (
                        <div
                          key={index}
                          className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                            <div>
                              <label
                                htmlFor={`finding_${index}`}
                                className="block text-xs font-medium text-gray-600 mb-1"
                              >
                                Finding
                              </label>
                              <input
                                type="text"
                                id={`finding_${index}`}
                                name="finding"
                                value={exam.finding}
                                onChange={(e) =>
                                  handleExaminationChange(e, index)
                                }
                                className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g., Chest examination"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor={`result_${index}`}
                                className="block text-xs font-medium text-gray-600 mb-1"
                              >
                                Result
                              </label>
                              <input
                                type="text"
                                id={`result_${index}`}
                                name="result"
                                value={exam.result}
                                onChange={(e) =>
                                  handleExaminationChange(e, index)
                                }
                                className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                                placeholder="e.g., Clear, no crepitations"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeExamination(index)}
                            className="px-3 py-1 text-red-600 hover:text-red-800 text-xs font-medium rounded-md hover:bg-red-50 transition-colors duration-150"
                          >
                            Remove Finding
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addExamination}
                        className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-sm font-medium shadow-sm"
                      >
                        Add Examination Finding
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Medicines Section */}
              <div className="mb-4">
                <label className="block text-lg font-semibold text-gray-800 mb-2">
                  Medicines
                </label>
                {formData.medicines.map((medicine, index) => (
                  <div
                    key={index}
                    className="p-3 border border-gray-200 rounded-md mb-3 bg-gray-50 shadow-sm"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
                      <div>
                        <label
                          htmlFor={`medicine_name_${index}`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          id={`medicine_name_${index}`}
                          name="name"
                          value={medicine.name}
                          onChange={(e) => handleInputChange(e, index)}
                          onFocus={() => {
                            setSearchingIndex(index);
                            setSearch("");
                          }}
                          onBlur={() => {
                            setTimeout(() => {
                              setSearch("");
                              setSearchingIndex(null);
                            }, 200);
                          }}
                          className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="e.g., Azithromycin"
                        />
                        {search !== "" && searchingIndex === index && (
                          <div className="px-0 rounded-lg bg-white relative shadow-lg mt-2 w-full z-20 max-h-60 overflow-y-auto overflow-x-hidden side_bar">
                            {isSearching ? (
                              <div className="p-2 text-sm text-gray-500">
                                Searching...
                              </div>
                            ) : (
                              searchResults.length > 0 &&
                              searchResults.map((medData, ind) => (
                                <div
                                  key={ind}
                                  className="flex gap-4 items-center hover:bg-queueHover hover:text-white rounded-md py-2 px-4 cursor-pointer"
                                  onClick={() => {
                                    setFormData((prev) => {
                                      const updatedMedicines = [
                                        ...prev.medicines,
                                      ];
                                      updatedMedicines[index] = {
                                        ...updatedMedicines[index],
                                        name: medData.product_name,
                                      };
                                      return {
                                        ...prev,
                                        medicines: updatedMedicines,
                                      };
                                    });
                                    setSearch("");
                                    setSearchingIndex(null);
                                  }}
                                >
                                  <div>
                                    <p className="text-sm">
                                      {medData.product_name}
                                    </p>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor={`medicine_dosage_${index}`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Dosage
                        </label>
                        <input
                          type="text"
                          id={`medicine_dosage_${index}`}
                          name="dosage"
                          value={medicine.dosage}
                          onChange={(e) => handleInputChange(e, index)}
                          className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="1-0-1 / 5ml"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`medicine_duration_${index}`}
                          className="block text-xs font-medium text-gray-600 mb-1"
                        >
                          Duration
                        </label>
                        <input
                          type="text"
                          id={`medicine_duration_${index}`}
                          name="duration"
                          value={medicine.duration}
                          onChange={(e) => handleInputChange(e, index)}
                          className="mt-1 w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="e.g. 5 days , 2 weeks"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeMedicine(index)}
                      className="px-3 py-1 text-red-600 hover:text-red-800 text-xs font-medium rounded-md hover:bg-red-50 transition-colors duration-150"
                    >
                      Remove Medicine
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addMedicine}
                  className="mt-2 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 text-sm font-medium shadow-sm"
                >
                  Add Medicine
                </button>
              </div>

              {/* Advice Textarea */}
              <div className="mb-4">
                <label
                  htmlFor="advice"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Advice
                </label>
                <textarea
                  id="advice"
                  name="advice"
                  value={formData.advice}
                  onChange={handleInputChange}
                  className="mt-1 w-full p-2 border border-gray-300 rounded-md h-20 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g., Take complete rest. Drink plenty of fluids. Follow up after 3 days."
                />
              </div>
              {/* Disclaimer */}
              <p className="mt-1 text-xs text-gray-500">
                <span className="font-semibold">Note:</span> To make words bold,
                wrap them in a single asterisk like <code>*example*</code>. For
                a new line, press <span className="font-semibold">Enter</span>{" "}
                (you can also type <code>\n</code>).
              </p>

              {/* Signature Upload Section */}
              <div className="mb-4">
                <label
                  htmlFor="signatureUpload"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Doctor's Signature
                </label>
                <input
                  type="file"
                  id="signatureUpload"
                  accept="image/*"
                  onChange={handleSignatureFileChange}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {signaturePreviewUrl && (
                  <div className="mt-2 border border-gray-300 rounded-md p-2 inline-block">
                    <img
                      src={signaturePreviewUrl}
                      alt="Signature Preview"
                      className="h-20 object-contain"
                    />
                  </div>
                )}
                {isUploadingSignature && (
                  <p className="text-sm text-indigo-600 mt-1">
                    Uploading signature...
                  </p>
                )}
              </div>
            </div>
            <div className=" px-2 w-full mt-4">
              <label className="block text-sm font-medium">
                Modify with <span className="text-blue-600">AI</span>
              </label>
              <div className="flex mt-1">
                <input
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                  placeholder="e.g. Add data for cough and cold"
                  className="flex-1 p-2 border rounded"
                />
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="ml-2 p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded disabled:opacity-50"
                >
                  <BsStars size={20} color="#fff" />
                </button>
              </div>
            </div>
          </div>
          {preview && (
            <div className="w-full md:p-6 bg-[#F7F0E8] overflow-y-auto h-[70vh] side_bar">
              <h1
                className={
                  "text-[#3f4346] font-semibold mb-4 " +
                  (preview ? "hidden md:flex !w-full lg:!w-full" : "")
                }
              >
                Preview
              </h1>
              <div className="flex justify-start md:justify-center">
                <PrescriptionPdf
                  hospData={hospData}
                  booking={bookings}
                  docDetails={docDetails}
                  medicines={formData.medicines}
                  complaints={formData.complaints}
                  historyAndExaminations={formData.historyAndExaminations}
                  diagnosis={formData.diagnosis}
                  investigations={formData.investigations}
                  previousMedicines={formData.previousMedicines}
                  advice={formData.advice}
                  signatureImageUrl={formData.signature}
                  age={age}
                  gender={gender}
                  patientName={patientCustomName}
                  vitals={formData.vitals}
                  patientContactNumber={patientContactNumber || undefined}
                  examinations={formData.examinations}
                />
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-3 flex flex-row items-center justify-end border-t-[0.5px] border-sbBorder">
          <WhiteButton
            name={preview ? "Close Preview" : "Close"}
            handleClick={() => (preview ? setPreview(!preview) : closeModal())}
            className="w-max h-10 md:h-12"
          />
          <DarkBlueButton
            name={preview ? "Send" : "Preview"}
            handleClick={() => (preview ? generatePdf() : setPreview(!preview))}
            type="button"
            className="w-max h-10 md:h-12"
            isLoading={loading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SendPrescriptionModal;
